import { message } from 'antd';
import { RecordInfoDrawer } from 'components/RecordInfoDrawer';
import { TableHeader } from 'components/TableHeader';
import { FETCH_ERROR, REQUEST_LOADING } from 'constants/alerts';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useDrawers } from 'hooks/useDrawers';
import { useFetch } from 'hooks/useFetch';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PermissionsService } from 'services/PermissionsService';
import { formatDate } from 'utils';
import { PermissionsForm } from './PermissionsForm';
import { PermissionsTable } from './PermissionsTable';

function Permissions() {
  useDocumentTitle('Доступы');
  const [permissions, setPermissions] = useState([]);
  const [modules] = useFetch('rbac/v1/modules/?page=1&size=50');
  const [loading, setLoading] = useState(false);
  const {
    state: { drawerVisible, activeRecord, formIsActive },
    openDrawer,
    openCreateForm,
    openEditForm,
    closeForm,
    closeDrawer,
  } = useDrawers();
  const { page, perPage, totalRows, setPagination, setTotalRows } =
    usePagination();

  const { setCount } = useHeaderChanger('Доступы');

  const recordFields = useMemo(() => {
    let moduleName = '';

    if (modules?.items && modules?.items?.length) {
      moduleName = modules?.items?.find(
        (module) => module.id === activeRecord?.module_id
      )?.name;
    }

    return [
      { label: 'Наименование', key: 'name' },
      { label: 'Slug', key: 'slug' },
      {
        label: 'Модуль',
        key: 'module_id',
        computedValue: moduleName,
      },
      { label: 'Функция', key: 'function' },
      {
        label: 'Дата создания',
        key: 'created',
        computedValue: formatDate(activeRecord?.created),
      },
    ];
  }, [activeRecord, modules]);

  const fetchPermissions = useCallback(async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await PermissionsService.getPermissions({
        page,
        perPage,
      });

      setPermissions(items);
      setCount(total);
      setTotalRows(total);
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  }, [page, perPage]);

  useEffect(() => fetchPermissions(), [fetchPermissions]);

  const handleDelete = async () => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isDeleted = await PermissionsService.deletePermission(
        activeRecord?.id
      );

      if (isDeleted) {
        closeDrawer();
        fetchPermissions();
        message.success('Доступ успешно удален');
      }
    } finally {
      hide();
    }
  };

  return (
    <>
      <TableHeader onCreate={openCreateForm} pageId="permissions" />

      <PermissionsTable
        items={permissions}
        modules={modules?.items}
        loading={loading}
        current={page}
        pageSize={perPage}
        totalRows={totalRows}
        onRowClick={openDrawer}
        onChange={setPagination}
      />

      <PermissionsForm
        isActive={formIsActive}
        modules={modules?.items}
        initialValues={activeRecord}
        onSuccess={fetchPermissions}
        onClose={closeForm}
      />

      <RecordInfoDrawer
        record={activeRecord}
        drawerTitle={activeRecord?.name}
        pageId="permissions"
        recordFields={recordFields}
        isVisible={drawerVisible}
        onClose={closeDrawer}
        onEdit={openEditForm}
        onDelete={handleDelete}
        withActions={false}
        deleteConfirmText="Вы точно хотите удалить этот доступ ?"
      />
    </>
  );
}

export { Permissions };
